import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getSiteSettings, updateUserTabs, updateProviderTabs, updatePublicSections
} from '../../services/adminApi';
import { toast } from 'react-toastify';

const Toggle = ({ on, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onChange}
    style={{
      width: 40, height: 22, background: on ? 'var(--accent)' : 'var(--surface3)',
      border: 'none', borderRadius: 11, cursor: disabled ? 'default' : 'pointer',
      position: 'relative', transition: 'background .2s', padding: 0,
      opacity: disabled ? .5 : 1
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: on ? 20 : 3,
      width: 16, height: 16, background: '#fff', borderRadius: '50%',
      transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)'
    }} />
  </button>
);

function TabConfigSection({ title, description, tabs, onChange, onSave, saving }) {
  return (
    <div className="admin-table-wrap" style={{ marginBottom: 20 }}>
      <div className="admin-table-header">
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text3)' }}>{description}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
          {saving ? <span className="spinner" /> : 'Save'}
        </button>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Tab</th>
            <th>Custom Label</th>
            <th style={{ textAlign: 'center' }}>Visible</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(tabs).map(([key, config]) => (
            <tr key={key}>
              <td>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text2)' }}>{key}</span>
              </td>
              <td>
                <input
                  className="admin-input"
                  style={{ maxWidth: 200 }}
                  value={config.label || ''}
                  onChange={e => onChange(key, { ...config, label: e.target.value })}
                  maxLength={32}
                />
              </td>
              <td style={{ textAlign: 'center' }}>
                <Toggle
                  on={config.enabled !== false}
                  onChange={() => onChange(key, { ...config, enabled: !config.enabled })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionsConfig({ title, description, sections, onChange, onSave, saving }) {
  return (
    <div className="admin-table-wrap" style={{ marginBottom: 20 }}>
      <div className="admin-table-header">
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text3)' }}>{description}</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
          {saving ? <span className="spinner" /> : 'Save'}
        </button>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Section</th>
            <th style={{ textAlign: 'center' }}>Visible</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(sections).map(([key, config]) => (
            <tr key={key}>
              <td style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</td>
              <td style={{ textAlign: 'center' }}>
                <Toggle
                  on={config.enabled !== false}
                  onChange={() => onChange(key, { ...config, enabled: !config.enabled })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getSiteSettings()
      .then(res => setSettings(res.data.settings))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUserTabChange = (key, val) => {
    setSettings(s => ({
      ...s,
      userTabs: { ...s.userTabs, [key]: val }
    }));
  };

  const handleProviderTabChange = (key, val) => {
    setSettings(s => ({
      ...s,
      providerTabs: { ...s.providerTabs, [key]: val }
    }));
  };

  const handlePublicSectionChange = (key, val) => {
    setSettings(s => ({
      ...s,
      publicProfileSections: { ...s.publicProfileSections, [key]: val }
    }));
  };

  const saveUserTabs = async () => {
    setSaving(s => ({ ...s, user: true }));
    try {
      const res = await updateUserTabs(settings.userTabs);
      setSettings(res.data.settings);
      toast.success('User tab settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(s => ({ ...s, user: false })); }
  };

  const saveProviderTabs = async () => {
    setSaving(s => ({ ...s, provider: true }));
    try {
      const res = await updateProviderTabs(settings.providerTabs);
      setSettings(res.data.settings);
      toast.success('Provider tab settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(s => ({ ...s, provider: false })); }
  };

  const savePublicSections = async () => {
    setSaving(s => ({ ...s, public: true }));
    try {
      const res = await updatePublicSections(settings.publicProfileSections);
      setSettings(res.data.settings);
      toast.success('Public profile settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(s => ({ ...s, public: false })); }
  };

  if (loading) {
    return (
      <AdminLayout title="Settings">
        <div className="empty-state"><div className="spinner" /></div>
      </AdminLayout>
    );
  }

  if (!settings) {
    return (
      <AdminLayout title="Settings">
        <div style={{ background: 'var(--red-dim)', border: '1px solid #7f1d1d', color: 'var(--red)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
          ⚠ Failed to load settings. The settings collection may not be initialised — restart the backend to auto-create it.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings">
      <div style={{ maxWidth: 740 }}>

        <div style={{ marginBottom: 20, padding: '14px 18px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text2)' }}>
            Control which tabs and sections are visible to registered users and service providers.
            Disabling a tab hides it from the dashboard but does not delete any data.
            You can also rename tabs by editing the label.
          </p>
        </div>

        <TabConfigSection
          title="User Dashboard Tabs"
          description="Tabs shown on the user's main dashboard"
          tabs={settings.userTabs}
          onChange={handleUserTabChange}
          onSave={saveUserTabs}
          saving={saving.user}
        />

        <TabConfigSection
          title="Provider Dashboard Tabs"
          description="Tabs shown on the provider's dashboard"
          tabs={settings.providerTabs}
          onChange={handleProviderTabChange}
          onSave={saveProviderTabs}
          saving={saving.provider}
        />

        <SectionsConfig
          title="Public Profile Sections"
          description="Sections available on public QR code profile pages (users still control individual field visibility)"
          sections={settings.publicProfileSections}
          onChange={handlePublicSectionChange}
          onSave={savePublicSections}
          saving={saving.public}
        />

      </div>
    </AdminLayout>
  );
}
